import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Strings} from '@github-ui/filter-core'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'
import {testIdProps} from '@github-ui/test-id-props'
import {useAnalytics} from '@github-ui/use-analytics'
import {Banner} from '@primer/react'
import {useEffect} from 'react'

import styles from './ValidationMessage.module.css'

interface ValidationMessageProps {
  id: string
  messages?: string[]
}

export const ValidationMessage = ({id, messages}: ValidationMessageProps) => {
  const {sendAnalyticsEvent} = useAnalytics()
  const useInlineSanitizePreset = Boolean(messages?.length) && isFeatureEnabled('sanitize_preset_validation_message')

  useEffect(() => {
    if (messages && messages.length > 0) {
      const userFriendlyMessage = messages
        // Handle pre while the flag can be disabled in case a producer was missed; remove the fallback when it graduates.
        .map(message => message.replaceAll(useInlineSanitizePreset ? /<\/?code>/g : /<\/?(?:pre|code)>/g, "'"))
        .join('. ')
        .trim()
      const announcement = `${Strings.filterInvalid(messages.length)} ${userFriendlyMessage}.`
      announce(announcement)
      sendAnalyticsEvent('filter.validation_errors', 'FILTER_VALIDATION_ERRORS', {messages: userFriendlyMessage})
    }
  }, [sendAnalyticsEvent, messages, useInlineSanitizePreset])

  if (!messages || messages.length < 1) return null

  return (
    <Banner id={id} variant="warning" className={styles.Banner}>
      <Banner.Title>
        <span {...testIdProps('validation-error-count')}>{Strings.filterInvalid(messages.length)}</span>
      </Banner.Title>
      <div className={styles.Box_0}>
        <ul className={styles.Box_2} {...testIdProps('validation-error-list')}>
          {messages.map(message => (
            <li key={message.replaceAll(' ', '-')}>
              {useInlineSanitizePreset ? (
                <UnsafeHTMLText html={message} preset="inline" className={styles.VerifiedHTMLBox_0} />
              ) : (
                <UnsafeHTMLText
                  html={message}
                  /* eslint-disable-next-line @github-ui/github-monorepo/no-custom-sanitize-config --
                     Existing custom sanitizer config; migrate to a named preset or VerifiedHTML when changed. */
                  domPurifyConfig={{ALLOWED_TAGS: ['code'], ALLOWED_ATTR: [], ALLOW_DATA_ATTR: false}}
                  className={styles.VerifiedHTMLBox_0}
                />
              )}
            </li>
          ))}
        </ul>
      </div>
    </Banner>
  )
}
